import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import VisitDetails from '../VisitaDetails';
import {
    usePatchProgramVisitsMutation
} from '../../../../services/visitasApi';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { TFunction } from 'i18next';
import { Visita } from '../../consts/visitasTypes';

type ProgramVisitDrawerProps = {
    selected: Visita | null;
    selectedVisitsIds?: number[];
    selectedVisitsSummary?: { id: number; label: string; subLabel?: string }[];
    setOpen: (value: boolean) => void;
    translate: TFunction;
    clearVisitsSelection?: () => void;
};

export const ProgramVisitDrawer = ({
    selected,
    selectedVisitsIds = [],
    selectedVisitsSummary = [],
    setOpen,
    translate,
    clearVisitsSelection
}: ProgramVisitDrawerProps) => {
    const dispatch = useAppDispatch();
    const [fecha, setFecha] = useState<Dayjs | null>(null);
    const [patchProgramVisits, { isLoading }] = usePatchProgramVisitsMutation();

    const visitIdsToProgram = useMemo(() => {
        if (selected && selectedVisitsIds.length === 0) {
            return [selected.id];
        }

        if (!selected && selectedVisitsIds.length > 0) {
            return selectedVisitsIds;
        }

        return selected ? [selected.id] : [];
    }, [selected, selectedVisitsIds]);

    const summaryById = useMemo(
        () => new Map(selectedVisitsSummary.map((v) => [v.id, v])),
        [selectedVisitsSummary]
    );

    const visitsToDisplay = useMemo(
        () =>
            visitIdsToProgram.map((id) => {
                const s = summaryById.get(id);
                return {
                    id,
                    label: s?.label || `#${id}`,
                    subLabel: s?.subLabel
                };
            }),
        [visitIdsToProgram, summaryById]
    );

    const isMultipleProgramming = !selected && visitIdsToProgram.length > 0;
    const selectedCount = visitIdsToProgram.length;
    const minSelectableDate = dayjs().startOf('day');
    const visitIdsSignature = visitIdsToProgram.join('-');

    useEffect(() => {
        setFecha(null);
    }, [selected?.id, visitIdsSignature]);

    const closeDrawer = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        if (!visitIdsToProgram.length) {
            return;
        }

        if (!fecha || fecha.isBefore(dayjs(), 'day')) {
            enqueueSnackbar(translate('drawerProgramVisit.snackbar.missingDate'), {
                variant: 'warning'
            });
            return;
        }

        try {
            await patchProgramVisits({
                idsVisita: visitIdsToProgram,
                fecha: fecha.toISOString()
            }).unwrap();

            enqueueSnackbar(
                selectedCount > 1
                    ? translate('drawerProgramVisit.snackbar.successMultiple', {
                          count: selectedCount
                      })
                    : translate('drawerProgramVisit.snackbar.successSingle'),
                {
                    variant: 'success'
                }
            );

            clearVisitsSelection?.();
            closeDrawer();
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudieron programar las visitas',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: '100%'
            }}
        >
            <DrawerHeader onIconClick={closeDrawer} text={translate('drawerProgramVisit.title')} />

            {isMultipleProgramming ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#F6F8FB',
                        border: '1px solid #DDE4EA'
                    }}
                >
                    <Typography fontWeight={700}>
                        {translate('drawerProgramVisit.selectedCount', {
                            count: selectedCount
                        })}
                    </Typography>
                    <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
                        {visitsToDisplay.map((visit) => (
                            <Box
                                key={visit.id}
                                sx={{
                                    py: 1,
                                    borderBottom: '1px solid #EEF1F4',
                                    '&:last-child': { borderBottom: 'none' }
                                }}
                            >
                                <Typography variant="body2" fontWeight={600} noWrap>
                                    {visit.label}{visit.subLabel ? ` · Cod SRT: ${visit.subLabel}` : ''}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {translate('drawerProgramVisit.sameDateInfo', { count: selectedCount })}
                    </Typography>
                </Box>
            ) : (
                selected && <VisitDetails visita={selected} />
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                    <DatePicker
                        label={translate('drawerProgramVisit.labelDate')}
                        value={fecha}
                        minDate={minSelectableDate}
                        onChange={(value) => setFecha(value)}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                size: 'small'
                            }
                        }}
                    />
                </LocalizationProvider>
                <Typography variant="body2" color="text.secondary">
                    {translate('drawerProgramVisit.dateInfo')}
                </Typography>
            </Box>

            <Box
                sx={{
                    mt: 'auto',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    pt: 2
                }}
            >
                <Button variant="outlined" onClick={closeDrawer}>
                    {translate('drawerProgramVisit.buttonCancel')}
                </Button>

                <Button variant="contained" onClick={handleConfirm} disabled={isLoading}>
                    {translate('drawerProgramVisit.buttonConfirm')}
                </Button>
            </Box>
        </Box>
    );
};
